import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Plus, Edit, Trash2, DollarSign, Building2, User, X, 
  MapPin, Loader2, AlertCircle, RefreshCw, Calendar, UserCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permission constants matching backend
const CREW_MANAGE_ROLES = ['operations_manager', 'admin', 'super_admin'];
const CAPACITY_READ_ROLES = [
  'sales_rep', 'sales', 'sales_manager', 'project_manager',
  'operations_manager', 'admin', 'super_admin', 'ceo',
  'president', 'leadership', 'general_manager', 'director_of_sales'
];

export const CrewManagement = () => {
  const { user } = useAuth();
  
  // Data state
  const [crews, setCrews] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  
  // Filter state
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [crewFormData, setCrewFormData] = useState({
    name: '',
    market_id: '',
    type: 'install',
    crew_type: 'W2',
    default_weekly_slots: 5,
    lead_user_id: '',
    member_user_ids: [],
    notes: '',
  });

  // Permission checks
  const canManageCrews = user?.role && CREW_MANAGE_ROLES.includes(user.role.toLowerCase());
  const canViewCrews = user?.role && CAPACITY_READ_ROLES.includes(user.role.toLowerCase());

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!canViewCrews) {
      setError('You do not have permission to view crew data.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build crews query
      const crewParams = new URLSearchParams();
      if (selectedMarket && selectedMarket !== 'all') {
        crewParams.append('market_id', selectedMarket);
      }
      if (!showInactive) {
        crewParams.append('is_active', 'true');
      }

      // Fetch crews, markets, and users in parallel
      const [crewsRes, marketsRes, usersRes] = await Promise.all([
        axios.get(`${API}/ops/crews?${crewParams.toString()}`),
        axios.get(`${API}/markets`),
        canManageCrews ? axios.get(`${API}/users`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);

      setCrews(crewsRes.data.crews || []);
      
      // Filter to only canonical active markets
      const canonicalMarkets = (marketsRes.data || []).filter(m => m.is_canonical && m.is_active);
      setMarkets(canonicalMarkets);
      
      setAllUsers(usersRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view crew data.');
      } else {
        setError('Failed to load crew data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedMarket, showInactive, canViewCrews, canManageCrews]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset form
  const resetForm = () => {
    setCrewFormData({
      name: '',
      market_id: markets[0]?.id || '',
      type: 'install',
      crew_type: 'W2',
      default_weekly_slots: 5,
      lead_user_id: '',
      member_user_ids: [],
      notes: '',
    });
  };

  // Open add dialog
  const handleOpenAddDialog = () => {
    if (!canManageCrews) {
      toast.error('You do not have permission to add crews.');
      return;
    }
    resetForm();
    setAddDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEditDialog = (crew) => {
    setSelectedCrew(crew);
    setCrewFormData({
      name: crew.name || '',
      market_id: crew.market_id || '',
      type: crew.type || 'install',
      crew_type: crew.crew_type || 'W2',
      default_weekly_slots: crew.default_weekly_slots || 5,
      lead_user_id: crew.lead_user_id || '',
      member_user_ids: crew.member_user_ids || [],
      notes: crew.notes || '',
    });
    setEditDialogOpen(true);
  };

  // Form change handler
  const handleCrewFormChange = (field, value) => {
    setCrewFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Add member to list
  const handleAddMember = (userId) => {
    if (!userId || crewFormData.member_user_ids.includes(userId)) return;
    if (userId === crewFormData.lead_user_id) {
      toast.error('Lead is already part of the crew.');
      return;
    }
    setCrewFormData(prev => ({
      ...prev,
      member_user_ids: [...prev.member_user_ids, userId],
    }));
  };

  // Remove member from list
  const handleRemoveMember = (userId) => {
    setCrewFormData(prev => ({
      ...prev,
      member_user_ids: prev.member_user_ids.filter(id => id !== userId),
    }));
  };

  // Create crew
  const handleCreateCrew = async () => {
    if (!crewFormData.name.trim()) {
      toast.error('Crew name is required.');
      return;
    }
    if (!crewFormData.market_id) {
      toast.error('Market is required.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/ops/crews`, {
        name: crewFormData.name.trim(),
        market_id: crewFormData.market_id,
        type: crewFormData.type,
        crew_type: crewFormData.crew_type,
        default_weekly_slots: crewFormData.default_weekly_slots,
        lead_user_id: crewFormData.lead_user_id || null,
        member_user_ids: crewFormData.member_user_ids,
        notes: crewFormData.notes.trim() || null,
      });
      toast.success('Crew created successfully.');
      setAddDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error creating crew:', err);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to create crews.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to create crew.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Update crew
  const handleUpdateCrew = async () => {
    if (!selectedCrew) return;
    if (!canManageCrews) {
      toast.error('You do not have permission to edit crews.');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`${API}/ops/crews/${selectedCrew.id}`, {
        name: crewFormData.name.trim(),
        market_id: crewFormData.market_id,
        type: crewFormData.type,
        crew_type: crewFormData.crew_type,
        default_weekly_slots: crewFormData.default_weekly_slots,
        lead_user_id: crewFormData.lead_user_id || null,
        member_user_ids: crewFormData.member_user_ids,
        notes: crewFormData.notes.trim() || null,
      });
      toast.success('Crew updated successfully.');
      setEditDialogOpen(false);
      setSelectedCrew(null);
      fetchData();
    } catch (err) {
      console.error('Error updating crew:', err);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to edit crews.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to update crew.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Delete (deactivate) crew
  const handleDeleteCrew = async () => {
    if (!selectedCrew) return;
    if (!canManageCrews) {
      toast.error('You do not have permission to delete crews.');
      return;
    }

    if (!window.confirm(`Are you sure you want to deactivate "${selectedCrew.name}"? This crew will no longer appear in capacity calculations.`)) {
      return;
    }

    setSaving(true);
    try {
      await axios.delete(`${API}/ops/crews/${selectedCrew.id}`);
      toast.success('Crew deactivated successfully.');
      setEditDialogOpen(false);
      setSelectedCrew(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting crew:', err);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to delete crews.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to deactivate crew.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Get user display name
  const getUserName = (userId) => {
    const foundUser = allUsers.find(u => u.id === userId);
    if (foundUser) {
      return `${foundUser.first_name || ''} ${foundUser.last_name || ''}`.trim() || foundUser.email;
    }
    return userId;
  };

  // Calculate totals
  const totalCrews = crews.length;
  const totalMembers = crews.reduce((sum, crew) => sum + (crew.member_count || 0), 0);
  const totalSlots = crews.reduce((sum, crew) => sum + (crew.default_weekly_slots || 0), 0);
  const w2Count = crews.filter(c => c.crew_type === 'W2').length;
  const contractorCount = crews.filter(c => c.crew_type === '1099').length;

  // Render loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading crews...</span>
        </div>
      </Layout>
    );
  }

  // Render error state
  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="crew-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Crew Management</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage installation crews, assignments, and weekly capacity</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData} size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {canManageCrews && (
              <Button onClick={handleOpenAddDialog} data-testid="add-crew-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add New Crew
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center gap-2">
            <Label htmlFor="market-filter" className="text-sm font-medium whitespace-nowrap">Market:</Label>
            <Select value={selectedMarket} onValueChange={setSelectedMarket}>
              <SelectTrigger id="market-filter" className="w-[180px]" data-testid="market-filter">
                <SelectValue placeholder="All Markets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Markets</SelectItem>
                {markets.map(market => (
                  <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
              data-testid="show-inactive-toggle"
            />
            <Label htmlFor="show-inactive" className="text-sm cursor-pointer">Show inactive crews</Label>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Crews</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="total-crews-count">{totalCrews}</div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{totalMembers} total members</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weekly Capacity</CardTitle>
              <Calendar className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="total-slots-count">{totalSlots}</div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">jobs per week</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Slots/Crew</CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {totalCrews > 0 ? (totalSlots / totalCrews).toFixed(1) : 0}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">per week</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">W2 vs 1099</CardTitle>
              <Building2 className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="crew-type-breakdown">
                {w2Count} / {contractorCount}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">W2 / Contractors</p>
            </CardContent>
          </Card>
        </div>

        {/* Crew Cards */}
        {crews.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Users className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Crews Found</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {selectedMarket !== 'all' 
                ? 'No crews found for the selected market.' 
                : 'Get started by adding your first crew.'}
            </p>
            {canManageCrews && (
              <Button onClick={handleOpenAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Crew
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {crews.map(crew => (
              <Card
                key={crew.id}
                onClick={() => handleOpenEditDialog(crew)}
                className={`cursor-pointer hover:shadow-lg transition-all group ${
                  !crew.is_active ? 'opacity-60 border-dashed' : ''
                }`}
                data-testid={`crew-card-${crew.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl group-hover:text-blue-600 dark:group-hover:text-blue-400 flex items-center gap-2">
                        {crew.name}
                        {!crew.is_active && (
                          <Badge variant="outline" className="text-xs">Inactive</Badge>
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 dark:text-gray-400">
                        <MapPin className="h-3 w-3" />
                        <span>{crew.market_name || 'Unknown Market'}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={crew.crew_type === 'W2' ? 'default' : 'secondary'}>
                        {crew.crew_type}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {crew.type}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Weekly Slots */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Weekly Slots</span>
                    </div>
                    <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {crew.default_weekly_slots}
                    </span>
                  </div>

                  {/* Lead Info */}
                  {crew.lead_user_name && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <UserCircle className="h-4 w-4 text-gray-400" />
                      <div>
                        <span className="text-xs text-gray-500 block">Lead</span>
                        <span className="text-sm font-medium">{crew.lead_user_name}</span>
                      </div>
                    </div>
                  )}

                  {/* Member Count */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">Members</span>
                    </div>
                    <span className="font-medium">{crew.member_count || 0}</span>
                  </div>

                  {/* Notes */}
                  {crew.notes && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic truncate">
                      {crew.notes}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Crew Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Add New Crew
              </DialogTitle>
              <DialogDescription>
                Create a new crew for capacity scheduling
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Crew Name */}
              <div className="space-y-2">
                <Label htmlFor="crew-name">Crew Name *</Label>
                <Input
                  id="crew-name"
                  value={crewFormData.name}
                  onChange={(e) => handleCrewFormChange('name', e.target.value)}
                  placeholder="e.g., Alpha Crew"
                  data-testid="crew-name-input"
                />
              </div>

              {/* Market */}
              <div className="space-y-2">
                <Label htmlFor="crew-market">Market *</Label>
                <Select
                  value={crewFormData.market_id}
                  onValueChange={(value) => handleCrewFormChange('market_id', value)}
                >
                  <SelectTrigger data-testid="crew-market-select">
                    <SelectValue placeholder="Select Market" />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Crew Type and Classification */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="crew-type">Crew Type</Label>
                  <Select
                    value={crewFormData.type}
                    onValueChange={(value) => handleCrewFormChange('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="install">Install</SelectItem>
                      <SelectItem value="repair">Repair</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="multi">Multi-Purpose</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="classification">Classification</Label>
                  <Select
                    value={crewFormData.crew_type}
                    onValueChange={(value) => handleCrewFormChange('crew_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="W2">W2 Employee</SelectItem>
                      <SelectItem value="1099">1099 Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Weekly Slots */}
              <div className="space-y-2">
                <Label htmlFor="weekly-slots">Default Weekly Slots *</Label>
                <Input
                  id="weekly-slots"
                  type="number"
                  min="1"
                  max="20"
                  value={crewFormData.default_weekly_slots}
                  onChange={(e) => handleCrewFormChange('default_weekly_slots', parseInt(e.target.value) || 5)}
                  placeholder="5"
                  data-testid="weekly-slots-input"
                />
                <p className="text-xs text-gray-500">Number of jobs this crew can handle per week</p>
              </div>

              <Separator />

              {/* Lead User */}
              {allUsers.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="lead-user">Crew Lead (Optional)</Label>
                  <Select
                    value={crewFormData.lead_user_id || 'none'}
                    onValueChange={(value) => handleCrewFormChange('lead_user_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Lead" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Lead</SelectItem>
                      {allUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Members */}
              {allUsers.length > 0 && (
                <div className="space-y-2">
                  <Label>Crew Members (Optional)</Label>
                  <Select onValueChange={handleAddMember}>
                    <SelectTrigger>
                      <SelectValue placeholder="Add member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allUsers
                        .filter(u => !crewFormData.member_user_ids.includes(u.id) && u.id !== crewFormData.lead_user_id)
                        .map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.first_name} {u.last_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {crewFormData.member_user_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {crewFormData.member_user_ids.map(userId => (
                        <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                          {getUserName(userId)}
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(userId)}
                            className="ml-1 hover:text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Input
                  id="notes"
                  value={crewFormData.notes}
                  onChange={(e) => handleCrewFormChange('notes', e.target.value)}
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateCrew} 
                disabled={saving || !crewFormData.name || !crewFormData.market_id}
                data-testid="create-crew-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Crew
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Crew Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit Crew: {selectedCrew?.name}
              </DialogTitle>
              <DialogDescription>
                Update crew details and assignments
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Crew Name */}
              <div className="space-y-2">
                <Label htmlFor="edit-crew-name">Crew Name *</Label>
                <Input
                  id="edit-crew-name"
                  value={crewFormData.name}
                  onChange={(e) => handleCrewFormChange('name', e.target.value)}
                  placeholder="e.g., Alpha Crew"
                  disabled={!canManageCrews}
                />
              </div>

              {/* Market */}
              <div className="space-y-2">
                <Label htmlFor="edit-crew-market">Market *</Label>
                <Select
                  value={crewFormData.market_id}
                  onValueChange={(value) => handleCrewFormChange('market_id', value)}
                  disabled={!canManageCrews}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Market" />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Crew Type and Classification */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-crew-type">Crew Type</Label>
                  <Select
                    value={crewFormData.type}
                    onValueChange={(value) => handleCrewFormChange('type', value)}
                    disabled={!canManageCrews}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="install">Install</SelectItem>
                      <SelectItem value="repair">Repair</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="multi">Multi-Purpose</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-classification">Classification</Label>
                  <Select
                    value={crewFormData.crew_type}
                    onValueChange={(value) => handleCrewFormChange('crew_type', value)}
                    disabled={!canManageCrews}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="W2">W2 Employee</SelectItem>
                      <SelectItem value="1099">1099 Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Weekly Slots */}
              <div className="space-y-2">
                <Label htmlFor="edit-weekly-slots">Default Weekly Slots *</Label>
                <Input
                  id="edit-weekly-slots"
                  type="number"
                  min="1"
                  max="20"
                  value={crewFormData.default_weekly_slots}
                  onChange={(e) => handleCrewFormChange('default_weekly_slots', parseInt(e.target.value) || 5)}
                  placeholder="5"
                  disabled={!canManageCrews}
                />
              </div>

              <Separator />

              {/* Lead User */}
              {allUsers.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="edit-lead-user">Crew Lead</Label>
                  <Select
                    value={crewFormData.lead_user_id || 'none'}
                    onValueChange={(value) => handleCrewFormChange('lead_user_id', value === 'none' ? '' : value)}
                    disabled={!canManageCrews}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Lead" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Lead</SelectItem>
                      {allUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Members */}
              {allUsers.length > 0 && canManageCrews && (
                <div className="space-y-2">
                  <Label>Crew Members</Label>
                  <Select onValueChange={handleAddMember}>
                    <SelectTrigger>
                      <SelectValue placeholder="Add member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allUsers
                        .filter(u => !crewFormData.member_user_ids.includes(u.id) && u.id !== crewFormData.lead_user_id)
                        .map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.first_name} {u.last_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {crewFormData.member_user_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {crewFormData.member_user_ids.map(userId => (
                        <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                          {getUserName(userId)}
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(userId)}
                            className="ml-1 hover:text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Input
                  id="edit-notes"
                  value={crewFormData.notes}
                  onChange={(e) => handleCrewFormChange('notes', e.target.value)}
                  placeholder="Any additional notes..."
                  disabled={!canManageCrews}
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              {canManageCrews && (
                <Button
                  variant="destructive"
                  onClick={handleDeleteCrew}
                  className="mr-auto"
                  disabled={saving}
                  data-testid="delete-crew-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Deactivate Crew
                </Button>
              )}
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
                {canManageCrews ? 'Cancel' : 'Close'}
              </Button>
              {canManageCrews && (
                <Button 
                  onClick={handleUpdateCrew} 
                  disabled={saving || !crewFormData.name || !crewFormData.market_id}
                  data-testid="save-crew-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Save Changes
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
